import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, Plus } from "lucide-react";
import { getCashoutHistory } from "../../services/walletService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import Pagination from "../../components/common/Pagination";
import CashoutRequestModal from "../../components/wallet/CashoutRequestModal";
import { color } from "../../styles/theme";

const PAGE_SIZE = 10;

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const statusVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("paid") || s.includes("complete") || s.includes("success")) return "success";
  if (s.includes("reject") || s.includes("fail")) return "danger";
  return "warning";
};

/**
 * Cashout History — ported from mobile's cash-out-history screen
 * (src/Screens/cash-out-history): paginated payout-request list + a Request
 * Cashout action (CashoutRequestModal), matching mobile's "+" FAB behavior.
 */
const CashoutHistory = () => {
  const { t } = useTranslation(["wallet", "common"]);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const load = (pageNumber = 1) => {
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    getCashoutHistory({ pageNumber, pageSize: PAGE_SIZE })
      .then((res) => {
        const list = Array.isArray(res.data?.Requests) ? res.data.Requests : [];
        setRequests(list);
        setMeta({ Page: res.data?.Page, PageSize: res.data?.PageSize, TotalCount: res.data?.TotalCount });
        setPage(pageNumber);
      })
      .catch(() => toast.error(t("cashoutHistory.loadError")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loader />;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate("/wallet")}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("cashoutHistory.backToWallet")}
      </button>
      <div style={s.headerRow}>
        <h2 style={s.title}>{t("cashoutHistory.title")}</h2>
        <Button icon={Plus} onClick={() => setShowRequestModal(true)}>{t("cashoutHistory.requestCashout")}</Button>
      </div>

      {requests.length === 0 ? (
        <EmptyState text={t("cashoutHistory.emptyState")} />
      ) : (
        <div style={s.list}>
          {requests.map((r, i) => (
            <Card key={r.CashoutRequestId ?? i} style={s.card}>
              <div style={s.row}><span style={s.label}>{t("cashoutHistory.amountLabel")}</span><span style={s.value}>{r.PromoterAmount} {r.Currency}</span></div>
              <div style={s.row}><span style={s.label}>{t("cashoutHistory.statusLabel")}</span><Badge variant={statusVariant(r.Status)}>{r.Status}</Badge></div>
              <div style={s.row}><span style={s.label}>{t("cashoutHistory.requestedLabel")}</span><span style={s.value}>{formatDate(r.RequestedAt)}</span></div>
            </Card>
          ))}
        </div>
      )}

      {requests.length > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={meta?.TotalCount ?? 0}
          onPageChange={(p) => load(p)}
          disabled={loadingMore}
        />
      )}

      {showRequestModal && (
        <CashoutRequestModal onClose={() => setShowRequestModal(false)} onRequested={() => { setShowRequestModal(false); load(1); }} />
      )}
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, padding: "6px 10px" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", margin: "12px 0 20px", flexWrap: "wrap", gap: 10 },
  title: { fontSize: 20, fontWeight: 800, color: color.textPrimary, margin: 0, letterSpacing: -0.3 },
  list: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 480 },
  card: { padding: "14px 16px" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, marginBottom: 7 },
  label: { color: color.textMuted },
  value: { fontWeight: 600, color: color.gray800 },
};

export default CashoutHistory;
